import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Package, Eye, Edit2, Trash2, ShoppingBag, Wrench, RefreshCw, RotateCcw, Printer } from 'lucide-react';
import usePermissions from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { getGoldPurityFactor, stonesTotalGrams } from '../../utils/calculations';
import { calculateGoldNetWeight } from '../../shared/utils/jewelryCalculations';
import { resolveImageUrl } from '../../utils/imageUrl';
import { todayCentral } from '../../shared/utils/dateUtils';

const ViewInventory = ({ onItemCodeClick, onEditItemClick }) => {
  const { hasPermission } = usePermissions();
  useAuth();

  const [jewelryPieces, setJewelryPieces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [currentGoldRate, setCurrentGoldRate] = useState(0);
  const [summaryStats, setSummaryStats] = useState({
    totalCostValue: 0,
    totalSaleValue: 0,
    totalAvailable: 0,
    totalSold: 0,
    totalReturn: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmUpdateModalOpen, setConfirmUpdateModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Stone inline typeahead state for edit modal
  const [editStoneSearch, setEditStoneSearch] = useState({});
  const [editStoneDropdownOpen, setEditStoneDropdownOpen] = useState({});
  const [editStoneHighlight, setEditStoneHighlight] = useState({});
  const editStoneInputRefs = useRef({});
  const lastAddedEditStoneId = useRef(null);

  // Repair modal states
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [repairItem, setRepairItem] = useState(null);
  const [repairForm, setRepairForm] = useState({
    repair_vendor_id: '', repair_sent_date: '', repair_expected_return: '',
    repair_cost_estimate: '', repair_notes: ''
  });
  const [returnRepairModalOpen, setReturnRepairModalOpen] = useState(false);
  const [returnRepairForm, setReturnRepairForm] = useState({
    repair_return_date: todayCentral(),
    repair_actual_cost: '', repair_notes: ''
  });
  const [repairSaving, setRepairSaving] = useState(false);


  // Image upload states
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFileInput, setImageFileInput] = useState(null);
  const [selectedCertificateFile, setSelectedCertificateFile] = useState(null);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [certificateFileInput, setCertificateFileInput] = useState(null);
  const [certificateDocuments, setCertificateDocuments] = useState([]);
  const [showCostPrice, setShowCostPrice] = useState(false);

  // Show cost price while backtick is held, hide on release
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === '`' && !e.repeat) setShowCostPrice(true); };
    const handleKeyUp = (e) => { if (e.key === '`') setShowCostPrice(false); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Scan & auto-link images
  const [scanningImages, setScanningImages] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [modalActivePhotoIdx, setModalActivePhotoIdx] = useState(0);

  // Repair handlers
  const handleOpenRepairModal = (item) => {
    setRepairItem(item);
    setRepairForm({
      repair_vendor_id: item.repair_vendor_id || '',
      repair_sent_date: todayCentral(),
      repair_expected_return: '',
      repair_cost_estimate: '',
      repair_notes: ''
    });
    setRepairModalOpen(true);
  };

  const handleSendToRepair = async () => {
    setRepairSaving(true);
    try {
      const res = await fetch(`/api/inventory/${repairItem.id}/send-to-repair`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(repairForm)
      });
      const data = await res.json();
      if (data.success) {
        setRepairModalOpen(false);
        loadInventoryData();
      } else {
        alert(data.error || 'Failed to send item to repair');
      }
    } catch (err) {
      alert('Error sending item to repair');
    } finally {
      setRepairSaving(false);
    }
  };

  const handleOpenReturnRepair = (item) => {
    setRepairItem(item);
    setReturnRepairForm({
      repair_return_date: todayCentral(),
      repair_actual_cost: item.repair_cost_estimate || '',
      repair_notes: ''
    });
    setReturnRepairModalOpen(true);
  };

  const handleReturnFromRepair = async () => {
    setRepairSaving(true);
    try {
      const res = await fetch(`/api/inventory/${repairItem.id}/return-from-repair`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(returnRepairForm)
      });
      const data = await res.json();
      if (data.success) {
        setReturnRepairModalOpen(false);
        if (viewModalOpen && selectedItem?.id === repairItem.id) setViewModalOpen(false);
        loadInventoryData();
      } else {
        alert(data.error || 'Failed to return item from repair');
      }
    } catch (err) {
      alert('Error returning item from repair');
    } finally {
      setRepairSaving(false);
    }
  };


  const handleScanImages = async () => {
    setScanningImages(true);
    setScanResult(null);
    try {
      const res = await fetch('/api/inventory/scan-images', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      setScanResult(data);
      if (data.linked > 0) {
        // Refresh inventory to show new images
        loadInventoryData();
      }
    } catch (err) {
      setScanResult({ success: false, error: err.message });
    } finally {
      setScanningImages(false);
    }
  };

  const loadInventoryData = useCallback(async () => {
    try {
      setSearching(true);

      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy: 'code',
        order: 'ASC'
      });

      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      if (searchTerm) {
        params.append('code_prefix', searchTerm);
      }

      const response = await fetch(`/api/inventory?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Inventory data loaded:', data);
        console.log('Items array:', data.items);
        console.log('Items length:', data.items?.length);
        setJewelryPieces(data.items || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || data.items?.length || 0,
          pages: data.pagination?.pages || Math.ceil((data.items?.length || 0) / pagination.limit) || 0
        }));
      } else {
        console.warn('Failed to load inventory data, status:', response.status);
        setJewelryPieces([]);
      }
    } catch (error) {
      console.error('Error loading inventory data:', error);
      setJewelryPieces([]);
    } finally {
      setSearching(false);
    }
  }, [pagination.page, pagination.limit, selectedCategory, selectedStatus, searchTerm]);

  // Load initial data (categories, vendors, materials)
  const loadSummaryStats = useCallback(async () => {
    try {
      const response = await fetch('/api/reports/dashboard', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setSummaryStats({
            totalCostValue: data.data.totalCostValue || 0,
            totalSaleValue: data.data.totalSaleValue || 0,
            totalAvailable: data.data.totalAvailable || 0,
            totalSold: data.data.totalSold || 0,
            totalReturn: data.data.totalReturn || 0,
          });
        }
      }
    } catch (error) {
      console.error('Error loading summary stats:', error);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCategories(),
        loadVendors(),
        loadMaterials(),
        loadSummaryStats(),
        fetch('/api/rates/gold/today', { credentials: 'include' })
          .then(r => r.json())
          .then(d => { if (d.success && d.rate?.gold_24k) setCurrentGoldRate(parseFloat(d.rate.gold_24k)); })
          .catch(() => { }),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  }, [loadSummaryStats]);

  // Load initial data on component mount
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Debounced search effect for inventory data
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      loadInventoryData();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, selectedCategory, selectedStatus, pagination.page, pagination.limit, loadInventoryData]);

  // Reset pagination when search or filters change
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, selectedStatus]);

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/categories?with_item_count=true', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadVendors = async () => {
    try {



      const response = await fetch('/api/vendors', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadMaterials = async () => {
    try {



      const response = await fetch('/api/materials', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      }
    } catch (error) {
      console.error('Error loading materials:', error);
    }
  };

  // Helper function to get material type from stones
  const getMaterialTypes = (stonesData) => {
    const parsedStones = parseStones(stonesData);
    if (!parsedStones || parsedStones.length === 0) return [];

    const materialTypes = new Set();
    parsedStones.forEach(stone => {
      if (stone.stoneCode) {
        const material = materials.find(m => m.code === stone.stoneCode);
        if (material && material.category) {
          materialTypes.add(material.category);
        }
      }
    });

    return Array.from(materialTypes);
  };

  // Helper function to safely parse stones data
  const parseStones = (stonesData) => {
    console.log('🔍 parseStones input:', stonesData, 'type:', typeof stonesData); // DEBUG LOG

    if (!stonesData) return [];

    // If already an array, map to expected fields
    if (Array.isArray(stonesData)) {
      console.log('📋 Processing stones array with', stonesData.length, 'items'); // DEBUG LOG
      return stonesData.map(stone => {
        const parsed = {
          stoneCode: stone.stone_code || stone.stoneCode || stone.code || '',
          stoneName: stone.stone_name || stone.stoneName || stone.name || '',
          weight: stone.weight || 0,
          rate: stone.cost_price || stone.rate || 0, // Use cost_price for inventory calculations
          stoneCost: (parseFloat(stone.weight || 0) * parseFloat(stone.cost_price || stone.rate || 0)) || 0,
          unit: stone.unit || 'gram', // Use stored unit, fallback to gram
        };
        console.log('💎 Parsed stone:', parsed); // DEBUG LOG
        return parsed;
      });
    }

    // If it's a string, try to parse it
    if (typeof stonesData === 'string') {
      try {
        const arr = JSON.parse(stonesData);
        console.log('📋 Parsed JSON stones:', arr); // DEBUG LOG
        return Array.isArray(arr) ? arr.map(stone => ({
          stoneCode: stone.stone_code || stone.stoneCode || stone.code || '',
          stoneName: stone.stone_name || stone.stoneName || stone.name || '',
          weight: stone.weight || 0,
          rate: stone.cost_price || stone.rate || 0, // Use cost_price for inventory calculations
          stoneCost: (parseFloat(stone.weight || 0) * parseFloat(stone.cost_price || stone.rate || 0)) || 0,
          unit: stone.unit || 'gram',
        })) : [];
      } catch (error) {
        console.error('❌ Error parsing stones JSON:', error); // DEBUG LOG
        return [];
      }
    }

    console.log('⚠️ Unhandled stones data format'); // DEBUG LOG
    return [];
  };

  // Helper function to highlight search terms
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.toString().split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Action handlers
  const handleViewItem = (item) => {
    console.log('Selected item:', item);
    console.log('Stones data:', item.stones);
    console.log('Stones type:', typeof item.stones);
    setSelectedItem(item);
    setModalActivePhotoIdx(0);
    setViewModalOpen(true);
  };

  const handleItemCodeClick = (item) => {
    // Same as view - show complete details
    handleViewItem(item);
  };

  const handleEditItem = (item) => {
    // Set up edit form data with all current values
    setSelectedItem(item);
    const parsedStones = parseStones(item.stones);
    setEditFormData({
      code: item.code || '',
      jewelry_name: item.name || '',
      description: item.description || '',
      vendor_id: item.vendor_id || '',
      category_id: item.category_id || '',
      gross_weight: item.gross_weight || '',
      net_weight: item.net_weight || '',
      gold_purity: item.gold_purity || '',
      gold_rate: (parseFloat(item.gold_rate) > 0 ? item.gold_rate : currentGoldRate) || '',
      fine_percentage: item.fine_percentage || '',
      kundan_weight: item.kundan_weight || '',
      kundan_percentage: item.kundan_percentage || '',
      making_charges: item.making_charges || '',
      labor_charges: item.labor_charges || '',
      other_costs: item.other_costs || '',
      wastage_charges: item.wastage_charges || '',
      wastage_percentage: item.wastage_percentage || '',
      total_cost_value: item.total_cost_value || '',
      sale_price: item.sale_price || '',
      certificate: item.certificate || 'No',
      status: item.status || 'In Stock',
      notes: item.notes || '',
      stones: parsedStones.map(s => ({ ...s, id: s.id || `${Date.now()}-${Math.random()}` })),
      image_url: item.image_url || null
    });
    setEditStoneSearch({});
    setEditStoneDropdownOpen({});
    setEditStoneHighlight({});
    // Set initial image preview if image exists
    setImagePreviewUrl(item.image_url || null);
    setCertificateDocuments(item.certificates || (item.documents || []).filter(document => document.document_type === 'certificate'));
    // Reset only the file selection states, not the preview URL
    setSelectedImageFile(null);
    setSelectedCertificateFile(null);
    setUploadingImage(false);
    if (imageFileInput) {
      imageFileInput.value = '';
    }
    setEditModalOpen(true);
  };

  const handleRemoveEditStone = (index) => {
    if (window.confirm('Are you sure you want to remove this stone?')) {
      const updatedStones = (editFormData.stones || []).filter((_, i) => i !== index);
      const totalStoneWeightGrams = stonesTotalGrams(updatedStones);
      const grossWeight = parseFloat(editFormData.gross_weight) || 0;
      const newNetWeight = calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight);
      setEditFormData(prev => ({
        ...prev,
        stones: updatedStones,
        stone_weight: totalStoneWeightGrams,
        net_weight: newNetWeight,
        total_stone_cost: updatedStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0)
      }));
    }
  };

  const handleDeleteItem = (item) => {
    setSelectedItem(item);
    setDeleteModalOpen(true);
  };

  const handleSaveEdit = () => {
    // Show confirmation modal instead of directly saving
    setConfirmUpdateModalOpen(true);
  };

  // Send the item's tag to the printer. Downloads an EPL file that the local
  // print agent watches for and prints on the TSC printer.
  const handlePrintTag = async (jewelryId, itemCode) => {
    try {
      const response = await fetch(`/api/labels/epl/${jewelryId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to generate tag');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${itemCode || jewelryId}.epl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Failed to print tag: ${error.message}`);
    }
  };

  const handleConfirmUpdate = async () => {
    if (!selectedItem) return;

    setSaving(true);
    setConfirmUpdateModalOpen(false);

    try {


      // RECALCULATE ALL VALUES PROPERLY
      const grossWeight = parseFloat(editFormData.gross_weight) || 0;
      const stones = editFormData.stones || [];

      // Calculate stone weight (convert all to grams)
      const totalStoneWeight = stonesTotalGrams(stones);

      const netWeight = calculateGoldNetWeight(grossWeight, totalStoneWeight, editFormData.kundan_weight);
      const goldRate = parseFloat(editFormData.gold_rate) || 0;
      const goldPurity = parseInt(editFormData.gold_purity) || 18;
      const purityFactor = getGoldPurityFactor(goldPurity);
      const wastagePercentage = parseFloat(editFormData.wastage_percentage) || 0;

      // Calculate gold price (WITHOUT wastage - calculate separately like display)
      const totalGoldCost = Math.ceil(netWeight * goldRate * purityFactor);
      const wastageCharges = Math.ceil(netWeight * goldRate * purityFactor * (wastagePercentage / 100));
      const totalGoldPrice = totalGoldCost + wastageCharges; // For backward compatibility with backend

      // Calculate stone cost
      const totalStoneCost = stones.reduce((total, stone) => {
        return total + (parseFloat(stone.stoneCost) || 0);
      }, 0);

      // Calculate making charges
      const makingChargesPerGram = parseFloat(editFormData.making_charges) || 0;
      const totalMakingCharges = Math.ceil(netWeight * makingChargesPerGram);

      // Calculate labor charges (per gram × net weight)
      const laborChargesPerGram = parseFloat(editFormData.labor_charges) || 0;
      const totalLaborCharges = Math.ceil(netWeight * laborChargesPerGram);

      // Additional costs
      const otherCosts = parseFloat(editFormData.other_costs) || 0;

      // Calculate certificate cost for diamonds/rounds
      let certificateCost = 0;
      if (editFormData.certificate === 'Yes') {
        const totalDiamondCarats = stones.reduce((total, stone) => {
          const material = materials.find(m => m.code === stone.stoneCode);
          const isDiamond = material ? material.category === 'Diamond'
            : !!(stone.stoneName && (stone.stoneName.toLowerCase().includes('diamond') ||
              stone.stoneName.toLowerCase().includes('round')));
          if (isDiamond) {
            let weight = parseFloat(stone.weight) || 0;
            // Convert to carats if needed
            if (stone.unit === 'gram') {
              weight = weight * 5; // 1 gram = 5 carats
            } else if (stone.unit === 'kg') {
              weight = weight * 5000;
            }
            return total + weight;
          }
          return total;
        }, 0);
        certificateCost = Math.ceil(totalDiamondCarats * 750);
      }

      // Calculate total cost value using same formula as display (line 1120)
      const totalCostValue = totalGoldCost + totalStoneCost + totalMakingCharges + wastageCharges + totalLaborCharges + otherCosts + certificateCost;

      console.log('=== EDIT SAVE CALCULATIONS ===');
      console.log(`Gross Weight: ${grossWeight}g`);
      console.log(`Stone Weight: ${totalStoneWeight}g`);
      console.log(`Net Weight: ${netWeight}g`);
      console.log(`Gold Cost: ₹${totalGoldCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`Wastage Charges: ₹${wastageCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`Total Gold Price: ₹${totalGoldPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`Stone Cost: ₹${totalStoneCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`Making Charges: ₹${totalMakingCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`Labor Charges: ₹${totalLaborCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (₹${laborChargesPerGram}/g × ${netWeight}g)`);
      console.log(`Other Costs: ₹${otherCosts.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`Certificate Cost: ₹${certificateCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log(`TOTAL COST VALUE: ₹${totalCostValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      console.log('Components: GoldCost + Wastage + Stone + Making + Labor + Other + Certificate');
      console.log(`= ${totalGoldCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + ${wastageCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + ${totalStoneCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + ${totalMakingCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + ${totalLaborCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + ${otherCosts.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + ${certificateCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

      // Prepare the data with ALL calculated values
      const updateData = {
        code: editFormData.code,
        name: editFormData.jewelry_name || editFormData.code,
        description: editFormData.description,
        vendor_id: editFormData.vendor_id || null,
        category_id: parseInt(editFormData.category_id) || null,
        gross_weight: grossWeight,
        stone_weight: totalStoneWeight,
        net_weight: netWeight,
        gold_purity: goldPurity,
        gold_rate: goldRate,
        total_gold_price: totalGoldPrice,
        total_stone_cost: totalStoneCost,
        wastage_percentage: wastagePercentage,
        total_wastage: wastageCharges,
        making_charges: makingChargesPerGram,
        total_making_charges: totalMakingCharges,
        labor_charges: laborChargesPerGram, // Store per-gram rate
        other_costs: otherCosts,
        total_cost_value: totalCostValue,
        sale_price: parseFloat(editFormData.sale_price) || 0,
        certificate: editFormData.certificate || 'No',
        notes: editFormData.notes || '',
        stones: editFormData.stones || [],
        image_url: editFormData.image_url || null,
        fine_percentage: editFormData.fine_percentage ? parseFloat(editFormData.fine_percentage) : null,
        kundan_weight: editFormData.kundan_weight ? parseFloat(editFormData.kundan_weight) : null,
        kundan_percentage: editFormData.kundan_percentage ? parseFloat(editFormData.kundan_percentage) : null
      };

      console.log('Sending calculated update data:', updateData);

      const response = await fetch(`/api/inventory/${selectedItem.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await loadInventoryData(); // Refresh the list
        loadSummaryStats(); // Refresh totals
        setEditModalOpen(false);
        setSelectedItem(null);
        setEditFormData({});
        resetImageStates();
        alert('Jewelry item updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update item: ${errorData.message || errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Error updating item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    setSaving(true);
    try {

      const response = await fetch(`/api/inventory/${selectedItem.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await loadInventoryData(); // Refresh the list
        loadSummaryStats(); // Refresh totals
        setDeleteModalOpen(false);
        setSelectedItem(null);
        alert('Item deleted successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to delete item: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Image upload functions
  const handleImageFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert('Image file size must be less than 5MB');
      return;
    }

    setSelectedImageFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile || !selectedItem) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImageFile);
      formData.append('itemCode', selectedItem.code);

      const response = await fetch('/api/inventory/upload-image', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        // Update form data with new image URL
        setEditFormData(prev => ({ ...prev, image_url: result.imageUrl }));
        // Update preview to show uploaded image
        setImagePreviewUrl(result.imageUrl);
        // Clear file selection
        setSelectedImageFile(null);
        if (imageFileInput) {
          imageFileInput.value = '';
        }
        alert('Image uploaded successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to upload image: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageButtonClick = () => {
    if (imageFileInput) {
      imageFileInput.click();
    }
  };

  const handleCertificateFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a PDF, JPEG, PNG, or WebP certificate file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Certificate file size must be less than 10MB');
      return;
    }

    setSelectedCertificateFile(file);
  };

  const handleCertificateUpload = async () => {
    if (!selectedCertificateFile || !selectedItem) return;

    setUploadingCertificate(true);
    try {
      const formData = new FormData();
      formData.append('document', selectedCertificateFile);
      formData.append('documentType', 'certificate');

      const response = await fetch(`/api/inventory/${selectedItem.id}/upload-document`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to upload certificate');
      }

      setCertificateDocuments(prev => [...prev, result.document]);
      setSelectedCertificateFile(null);
      if (certificateFileInput) certificateFileInput.value = '';
      alert('Certificate uploaded successfully!');
    } catch (error) {
      console.error('Error uploading certificate:', error);
      alert(`Error uploading certificate: ${error.message}`);
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleRemoveImage = () => {
    if (window.confirm('Are you sure you want to remove this image?')) {
      setEditFormData(prev => ({ ...prev, image_url: null }));
      setImagePreviewUrl(null);
      setSelectedImageFile(null);
      if (imageFileInput) {
        imageFileInput.value = '';
      }
    }
  };

  // Reset image states when modal closes
  const resetImageStates = () => {
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setUploadingImage(false);
    setSelectedCertificateFile(null);
    setUploadingCertificate(false);
    setCertificateDocuments([]);
    if (imageFileInput) {
      imageFileInput.value = '';
    }
    if (certificateFileInput) {
      certificateFileInput.value = '';
    }
  };

  // Permission checks
  const canView = hasPermission('view-inventory', 'view');
  const canEdit = hasPermission('edit-inventory', 'edit');
  const canDelete = hasPermission('edit-inventory', 'delete');

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">Access Denied</div>
          <div className="text-gray-600">You don't have permission to view inventory.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-blue-500 text-xl mb-2">Loading...</div>
          <div className="text-gray-600">Loading inventory data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto pb-12">
      <PageIdentifier pageId={SCREEN_IDS?.INVENTORY?.VIEW_LIST || 'INV-001'} pageName="View Inventory" />
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Inventory Management</h1>
            <p className="text-gray-600">View and manage your jewelry inventory</p>
          </div>
          {/* Scan & Auto-link Images */}
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={handleScanImages}
              disabled={scanningImages}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg text-sm font-medium transition-colors"
              title="Scans uploads/jewelry-images/ for files named <tag>.jpg and links them to inventory items"
            >
              {scanningImages ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
              {scanningImages ? 'Scanning...' : 'Scan & Link Images'}
            </button>
            {scanResult && (
              <p className={`text-xs ${scanResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {scanResult.success
                  ? `✓ ${scanResult.linked} photo${scanResult.linked !== 1 ? 's' : ''} linked to ${scanResult.linkedItems || scanResult.linked} item${(scanResult.linkedItems || scanResult.linked) !== 1 ? 's' : ''}`
                  : `✗ ${scanResult.error}`}
              </p>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 mb-6">

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Available Stock</p>
                <p className="text-4xl font-bold mt-2">{summaryStats.totalAvailable}</p>
                <p className="text-blue-100 text-xs mt-1">Items in stock</p>
              </div>
              <Package className="text-blue-200" size={48} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Sold</p>
                <p className="text-4xl font-bold mt-2">{summaryStats.totalSold}</p>
                <p className="text-green-100 text-xs mt-1">
                  Items sold
                  {(summaryStats.totalAvailable + summaryStats.totalSold + summaryStats.totalReturn) > 0 && (
                    <span className="ml-1">
                      ({Math.round((summaryStats.totalSold / (summaryStats.totalAvailable + summaryStats.totalSold + summaryStats.totalReturn)) * 100)}%)
                    </span>
                  )}
                </p>
              </div>
              <ShoppingBag className="text-green-200" size={48} />
            </div>
          </div>
        </div>

        {/* Enhanced Search and Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Search & Filter Jewelry Items</h3>
            <p className="text-sm text-gray-600">Find items whose code, name, description, category, or vendor starts with your search</p>
          </div>

          <div className="space-y-4">
            {/* Primary Search Bar */}
            <div className="relative">
              {searching ? (
                <div className="absolute left-3 top-3 h-5 w-5">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              )}
              <input
                type="text"
                placeholder="Starts with item code, name, description, category, or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="min-w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-40">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Sold">Sold</option>
                  <option value="Reserved">Reserved</option>
                  <option value="Under Repair">Under Repair</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>

              {/* Search Results Count */}
              {searchTerm && (
                <div className="flex items-center text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                  {searching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span>Searching for "{searchTerm}"...</span>
                    </>
                  ) : (
                    <>
                      <span className="font-medium">{jewelryPieces.length}</span>
                      <span className="ml-1">items found for "{searchTerm}"</span>
                    </>
                  )}
                </div>
              )}

              {/* Clear All Filters */}
              {(searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setSelectedStatus('all');
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Clear All Filters
                </button>
              )}

            </div>

            {/* Quick Search Suggestions */}
            {!searchTerm && (
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-500">Quick search:</span>
                {['18K', '22K', 'Diamond', 'Gold', 'In Stock', 'Necklace', 'Ring'].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => setSearchTerm(suggestion)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-full text-gray-700"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Table Header with Search Results */}
          {searchTerm && (
            <div className="bg-blue-50 px-6 py-3 border-b border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Search className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-blue-800 font-medium">
                    Search Results for "{searchTerm}"
                  </span>
                </div>
                <span className="text-blue-600 text-sm">
                  {jewelryPieces.length} of {pagination.total} items
                </span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">Photo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Weight</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Weight</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fine Net Wt</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  {(canEdit || canDelete) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jewelryPieces.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {item.image_url ? (
                        <img
                          src={resolveImageUrl(item.image_url)}
                          alt={item.code}
                          className="w-10 h-10 object-contain rounded border border-gray-200 bg-white"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <button
                        onClick={() => handleItemCodeClick(item)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        title="View item details"
                      >
                        {highlightSearchTerm(item.code, searchTerm)}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.gross_weight ? `${parseFloat(item.gross_weight).toFixed(3)}g` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.net_weight ? `${parseFloat(item.net_weight).toFixed(3)}g` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.fine_net_weight != null ? `${parseFloat(item.fine_net_weight).toFixed(3)}g` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {highlightSearchTerm(item.category_code || (item.code ? item.code.split('-')[0] : 'N/A'), searchTerm)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate" title={item.description}>
                      {highlightSearchTerm(item.description || 'No description', searchTerm)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                        item.status === 'Sold' ? 'bg-red-100 text-red-800' :
                          item.status === 'Reserved' ? 'bg-yellow-100 text-yellow-800' :
                            (item.status === 'Under Repair' || item.status === 'Repair') ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                        }`}>
                        {item.status || 'In Stock'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {highlightSearchTerm(item.vendor_code || 'N/A', searchTerm)}
                    </td>
                    {(canEdit || canDelete) && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewItem(item)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handlePrintTag(item.id, item.code)}
                            className="text-gray-600 hover:text-amber-600"
                            title="Print Tag"
                          >
                            <Printer size={16} />
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => handleEditItem(item)}
                              className="text-green-600 hover:text-green-900"
                              title="Edit Item"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                          {canEdit && item.status === 'In Stock' && (
                            <button
                              onClick={() => handleOpenRepairModal(item)}
                              className="text-blue-500 hover:text-blue-800"
                              title="Send to Repair"
                            >
                              <Wrench size={16} />
                            </button>
                          )}
                          {canEdit && (item.status === 'Under Repair' || item.status === 'Repair') && (
                            <button
                              onClick={() => handleOpenReturnRepair(item)}
                              className="text-teal-600 hover:text-teal-900"
                              title="Return from Repair"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteItem(item)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Item"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {jewelryPieces.length === 0 && (
              <div className="text-center py-12">
                {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all' ? (
                  <>
                    <Search className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No items found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your search terms or filters
                    </p>
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedCategory('all');
                          setSelectedStatus('all');
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory items</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by adding a new item to your inventory.</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                    <span className="font-medium">{pagination.pages}</span> ({pagination.total} total items)
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      {viewModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <PageIdentifier pageId={SCREEN_IDS?.INVENTORY?.VIEW_DETAILS_MODAL || 'INV-001M1'} pageName="View Item Details" isModal={true} />
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Item Details</h3>
                <div className="flex items-center space-x-2">
                  {canEdit && (
                    <button
                      onClick={() => {
                        setViewModalOpen(false);
                        handleEditItem(selectedItem);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-1"
                    >
                      <Edit2 size={16} />
                      <span>Edit</span>
                    </button>
                  )}
                  <button
                    onClick={() => setViewModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Half - Item Details */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Item Code</label>
                      <p className="text-sm text-gray-900">{selectedItem.code}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vendor</label>
                      <p className="text-sm text-gray-900">{selectedItem.vendor_code || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <p className="text-sm text-gray-900">{selectedItem.description || 'No description available'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Gold Purity</label>
                      <p className="text-sm text-gray-900">{selectedItem.gold_purity ? `${selectedItem.gold_purity}K` : 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Gross Weight</label>
                      <p className="text-sm text-gray-900">{selectedItem.gross_weight ? `${parseFloat(selectedItem.gross_weight).toFixed(3)}g` : 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <p className="text-sm text-gray-900">
                        {(() => {
                          const materialTypes = getMaterialTypes(selectedItem.stones);
                          const jewelType = selectedItem.category_code || (selectedItem.code ? selectedItem.code.split('-')[0] : 'N/A');
                          const materialTypeStr = materialTypes.length > 0 ? materialTypes.join(', ') : '';
                          return materialTypeStr ? `${materialTypeStr} + ${jewelType}` : jewelType;
                        })()}
                      </p>
                      {selectedItem.category_description && (
                        <p className="text-xs text-gray-500 mt-1">{selectedItem.category_description}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Net Weight</label>
                      <p className="text-sm text-gray-900">{selectedItem.net_weight ? `${parseFloat(selectedItem.net_weight).toFixed(3)}g` : 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fine Net Wt</label>
                      <p className="text-sm text-gray-900">{selectedItem.fine_net_weight != null ? `${parseFloat(selectedItem.fine_net_weight).toFixed(3)}g` : 'N/A'}</p>
                    </div>
                    {selectedItem.kundan_weight != null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Kundan Weight</label>
                        <p className="text-sm text-gray-900 font-medium text-amber-700">{parseFloat(selectedItem.kundan_weight).toFixed(3)}g</p>
                      </div>
                    )}
                    {selectedItem.fine_percentage != null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Fine %</label>
                        <p className="text-sm text-gray-900 font-medium text-yellow-700">{parseFloat(selectedItem.fine_percentage).toFixed(2)}%</p>
                      </div>
                    )}
                    {selectedItem.kundan_percentage != null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Kundan %</label>
                        <p className="text-sm text-gray-900 font-medium text-amber-700">{parseFloat(selectedItem.kundan_percentage).toFixed(2)}%</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${selectedItem.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                        selectedItem.status === 'Sold' ? 'bg-red-100 text-red-800' :
                          selectedItem.status === 'Reserved' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {selectedItem.status}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Certificate</label>
                      {(() => {
                        const certificates = Array.isArray(selectedItem.certificates)
                          ? selectedItem.certificates
                          : (selectedItem.documents || []).filter(document => document.document_type === 'certificate');

                        return certificates.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {certificates.map((document, index) => (
                              <span key={document.id || document.url || index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Certificate {index + 1} attached
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex mt-1 px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Certificate not attached
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Right Half - Image Gallery */}
                <div className="flex flex-col">
                  <h4 className="text-lg font-semibold text-gray-700 mb-3">
                    Item Photos
                    {(selectedItem?.images?.length || (selectedItem?.image_url ? 1 : 0)) > 0 && (
                      <span className="ml-2 text-sm font-normal text-gray-400">({selectedItem?.images?.length || 1})</span>
                    )}
                  </h4>
                  {(() => {
                    const storedImages = Array.isArray(selectedItem?.images) ? selectedItem.images : [];
                    const images = storedImages.length > 0 ? storedImages : (selectedItem?.image_url ? [{ url: selectedItem.image_url, is_primary: true }] : []);
                    const safePhotoIndex = modalActivePhotoIdx < images.length ? modalActivePhotoIdx : 0;
                    const activeUrl = resolveImageUrl(images[safePhotoIndex]?.url);
                    return images.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {/* Main large image */}
                        <div className="w-full h-60 border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                          <img
                            src={activeUrl}
                            alt={selectedItem.code}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <a href="${activeUrl}" target="_blank" rel="noopener noreferrer"
                                   class="flex flex-col items-center justify-center gap-2 text-blue-600 hover:text-blue-800 p-4 text-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                  <span class="text-sm font-medium">Image blocked by browser</span>
                                  <span class="text-xs underline">Click to open in new tab ↗</span>
                                </a>`;
                            }}
                          />
                        </div>
                        {/* Thumbnail strip (only if > 1 photo) */}
                        {images.length > 1 && (
                          <div className="flex gap-2 flex-wrap">
                            {images.map((img, idx) => (
                              <button
                                key={idx}
                                onClick={() => setModalActivePhotoIdx(idx)}
                                className={`w-14 h-14 rounded border-2 overflow-hidden flex-shrink-0 transition-colors ${idx === safePhotoIndex ? 'border-amber-500' : 'border-gray-200 hover:border-gray-400'}`}
                              >
                                <img src={resolveImageUrl(img.url)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-contain bg-white" onError={(e) => { e.target.style.display = 'none'; }} />
                              </button>
                            ))}
                          </div>
                        )}
                        <a href={activeUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline text-right">Open full size ↗</a>
                      </div>
                    ) : (
                      <div className="w-full h-60 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <div className="text-center text-gray-500">
                          <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">No image available</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {(() => {
                // DEBUG: Log stone data inspection
                console.log('🔍 ViewInventory Debug:');
                console.log('- selectedItem.stones exists:', !!selectedItem.stones);
                console.log('- selectedItem.stones value:', selectedItem.stones);
                console.log('- parseStones result:', parseStones(selectedItem.stones));
                console.log('- parseStones length:', parseStones(selectedItem.stones).length);

                const parsedStones = parseStones(selectedItem.stones);
                const shouldShowStones = selectedItem.stones && parsedStones.length > 0;
                console.log('- Should show stones section:', shouldShowStones);

                return shouldShowStones;
              })() && (
                  <div className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left Side - Stone Details */}
                      <div>
                        <h4 className="text-md font-medium text-gray-700 mb-3">Stones Details</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                                {showCostPrice && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost Rate (₹)</th>}
                                {showCostPrice && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost Value (₹)</th>}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {parseStones(selectedItem.stones).map((stone, index) => {
                                // Get material info to determine proper unit and cost calculation
                                const material = materials.find(m => m.code === stone.stoneCode);
                                const displayUnit = material ? material.unit : (stone.unit || 'CARAT');
                                // Use cost_price from database or materials table
                                const costPrice = material ? (material.cost_price || stone.cost_price || stone.rate) : (stone.cost_price || stone.rate);
                                const costValue = parseFloat(stone.weight || 0) * parseFloat(costPrice || 0);
                                const u = (displayUnit || '').toUpperCase();
                                const unitLabel = u === 'CARAT' || u === 'CARATS' || u === 'CT' ? 'Ct' : u === 'GRAM' || u === 'GRAMS' || u === 'G' ? 'g' : u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER' ? 'pcs' : displayUnit || 'Ct';

                                return (
                                  <tr key={index}>
                                    <td className="px-4 py-2 text-sm text-gray-900">{stone.stoneCode || stone.stone_code || 'N/A'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-900">{stone.stoneName || stone.stone_name || 'N/A'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-900">{stone.weight || 0}</td>
                                    <td className="px-4 py-2 text-sm text-gray-900">{unitLabel}</td>
                                    {showCostPrice && <td className="px-4 py-2 text-sm text-gray-900">₹{parseFloat(costPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>}
                                    {showCostPrice && <td className="px-4 py-2 text-sm text-green-600 font-medium">₹{parseFloat(costValue).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Right Side - Cost Breakdown */}
                      <div>
                        <div className="bg-gray-50 p-4 rounded-lg border">
                          <h4 className="text-md font-medium text-gray-700 mb-3">Cost Breakdown</h4>
                          <div style={{ lineHeight: '0.9', fontSize: '14px' }}>{(() => {
                            // Calculate breakdown components
                            // const grossWeight = parseFloat(selectedItem.gross_weight || 0); // Not used in calculations
                            const netWeight = parseFloat(selectedItem.net_weight || 0);
                            const storedRate = parseFloat(selectedItem.gold_rate || 0);
                            const goldRate = storedRate > 0 ? storedRate : currentGoldRate;
                            const goldPurity = parseInt(selectedItem.gold_purity || 18);
                            const purityFactor = getGoldPurityFactor(goldPurity);

                            // Calculate total gold cost (exact decimal, no ceiling)
                            const totalGoldCost = parseFloat((netWeight * goldRate * purityFactor).toFixed(2));

                            // Calculate total stone value from stones data (exact decimal per stone)
                            const stones = parseStones(selectedItem.stones);
                            const totalStoneValue = parseFloat(stones.reduce((total, stone) => {
                              const material = materials.find(m => m.code === stone.stoneCode);
                              // Use cost_price from database or materials table
                              const costPrice = stone.cost_price || stone.rate || (material?.cost_price || 0);
                              const costValue = parseFloat(stone.weight || 0) * parseFloat(costPrice || 0);
                              return total + costValue;
                            }, 0).toFixed(2));

                            // Calculate making charges
                            const makingChargesPerGram = parseFloat(selectedItem.making_charges || 0);
                            const totalMakingCharges = parseFloat((netWeight * makingChargesPerGram).toFixed(2));

                            // Calculate wastage charges
                            const wastagePercentage = parseFloat(selectedItem.wastage_percentage || 0);
                            const wastageCharges = parseFloat(((netWeight * (wastagePercentage / 100)) * goldRate * purityFactor).toFixed(2));

                            // Calculate certificate cost for diamonds
                            let certificateCost = 0;
                            if (selectedItem.certificate === 'Yes') {
                              const totalDiamondCarats = stones.reduce((total, stone) => {
                                const material = materials.find(m => m.code === stone.stoneCode);
                                const isDiamond = material ? material.category === 'Diamond'
                                  : !!(stone.stoneName && (stone.stoneName.toLowerCase().includes('diamond') ||
                                    stone.stoneName.toLowerCase().includes('round')));
                                if (isDiamond) {
                                  let weight = parseFloat(stone.weight || 0);
                                  const unit = material ? material.unit : (stone.unit || 'gram');

                                  // Convert to carats if needed
                                  if (unit === 'gram') {
                                    weight = weight * 5; // 1 gram = 5 carats
                                  } else if (unit === 'kg') {
                                    weight = weight * 5000;
                                  }
                                  return total + weight;
                                }
                                return total;
                              }, 0);
                              certificateCost = parseFloat((totalDiamondCarats * 750).toFixed(2));
                            }

                            // Additional costs
                            const laborChargesPerGram = parseFloat(selectedItem.labor_charges || 0);
                            const laborCharges = parseFloat((netWeight * laborChargesPerGram).toFixed(2));
                            const otherCosts = parseFloat(selectedItem.other_costs || 0);

                            const calculatedTotal = parseFloat((totalGoldCost + totalStoneValue + totalMakingCharges + wastageCharges + laborCharges + otherCosts + certificateCost).toFixed(2));

                            return (
                              <div style={{ margin: '0', padding: '0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                  <span style={{ fontSize: '14px', color: '#4B5563' }}>
                                    Total Gold Cost:
                                    {storedRate === 0 && currentGoldRate > 0 && (
                                      <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: '4px' }}>(live rate)</span>
                                    )}
                                  </span>
                                  <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{totalGoldCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                  <span style={{ fontSize: '14px', color: '#4B5563' }}>Total Stone Value:</span>
                                  <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{totalStoneValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                  <span style={{ fontSize: '14px', color: '#4B5563' }}>Total Making Charges:</span>
                                  <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{totalMakingCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                  <span style={{ fontSize: '14px', color: '#4B5563' }}>Labor Charges:</span>
                                  <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{laborCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                  <span style={{ fontSize: '14px', color: '#4B5563' }}>Other Costs:</span>
                                  <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{otherCosts.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                  <span style={{ fontSize: '14px', color: '#4B5563' }}>Wastage Charges:</span>
                                  <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{wastageCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                {certificateCost > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0', padding: '0', lineHeight: '16px', height: '16px' }}>
                                    <span style={{ fontSize: '14px', color: '#4B5563' }}>Certificate Cost:</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500' }}>₹{certificateCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                                <div style={{ borderTop: '1px solid #D1D5DB', margin: '1px 0', padding: '0' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1px 0', lineHeight: '16px', height: '18px' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Total Cost Value:</span>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#059669' }}>
                                      ₹{calculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
            <PageIdentifier pageId={SCREEN_IDS?.INVENTORY?.EDIT_MODAL || 'INV-001M2'} pageName="Edit Item Modal" isModal={true} />
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-medium text-gray-900">Edit Jewelry Item</h3>
                  <span
                    title="Status is set by billing and repair processes, not this form"
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${selectedItem.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                      selectedItem.status === 'Sold' ? 'bg-red-100 text-red-800' :
                        selectedItem.status === 'Reserved' ? 'bg-yellow-100 text-yellow-800' :
                          (selectedItem.status === 'Under Repair' || selectedItem.status === 'Repair') ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                      }`}
                  >
                    {selectedItem.status || 'In Stock'}
                    <span className="ml-1 font-normal text-[10px] opacity-70">(read only)</span>
                  </span>
                </div>
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Two-Thirds - Form Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Basic Information */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-700 mb-3">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Item Code <span className="text-gray-500">(Read Only)</span>
                        </label>
                        <input
                          type="text"
                          value={editFormData.code}
                          className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                          readOnly
                          disabled
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jewelry Name <span className="text-xs text-gray-400 font-normal">(shown in estimates &amp; billing)</span>
                        </label>
                        <input
                          type="text"
                          value={editFormData.jewelry_name}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, jewelry_name: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g. Diamond Bangles, Gold Necklace Set..."
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <input
                          type="text"
                          value={editFormData.description}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                        <select
                          value={editFormData.category_id}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, category_id: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Category</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
                        <select
                          value={editFormData.vendor_id}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, vendor_id: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Vendor</option>
                          {vendors.map(vendor => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </option>
                          ))}
                        </select>
                      </div>

                    </div>
                  </div>

                  {/* Weight and Gold Information */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-700 mb-3">Weight & Gold Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gross Weight (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          value={editFormData.gross_weight}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, gross_weight: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Net Weight (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          value={(() => {
                            // Calculate net weight = gross weight - total stone weight in grams
                            const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                            const stones = editFormData.stones || [];

                            // Calculate total stone weight in grams
                            const totalStoneWeightGrams = stonesTotalGrams(stones);

                            const netWeight = calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight);
                            return netWeight.toFixed(3);
                          })()}
                          readOnly
                          className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          title="Calculated automatically: Gross Weight - Total Stone Weight (in grams)"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gold Purity</label>
                        <select
                          value={editFormData.gold_purity}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, gold_purity: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Purity</option>
                          <option value="14">14KT</option>
                          <option value="18">18KT</option>
                          <option value="22">22KT</option>
                          <option value="24">24KT</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gold Rate (₹/g)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.gold_rate || ''}
                          onChange={(e) => {
                            setEditFormData(prev => ({ ...prev, gold_rate: e.target.value }));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fine %</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editFormData.fine_percentage || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, fine_percentage: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g. 58.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">Vendor fine % (used in purchase cost)</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kundan Weight (g)</label>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={editFormData.kundan_weight || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, kundan_weight: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.000"
                        />
                        <p className="text-xs text-gray-500 mt-1">Kundan gold weight in grams</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kundan %</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={editFormData.kundan_percentage || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, kundan_percentage: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g. 58.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">Kundan fine percentage</p>
                      </div>
                    </div>
                  </div>

                  {/* Charges and Pricing */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-700 mb-3">Charges & Pricing</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Making Charges (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.making_charges || ''}
                          onChange={(e) => {
                            setEditFormData(prev => ({ ...prev, making_charges: e.target.value }));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Labor Charges (₹ per gram)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.labor_charges || ''}
                          onChange={(e) => {
                            setEditFormData(prev => ({ ...prev, labor_charges: e.target.value }));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Other Costs (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.other_costs || ''}
                          onChange={(e) => {
                            setEditFormData(prev => ({ ...prev, other_costs: e.target.value }));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Wastage Charges (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.wastage_charges || ''}
                          onChange={(e) => {
                            setEditFormData(prev => ({ ...prev, wastage_charges: e.target.value }));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Certificate</label>
                        <select
                          value={editFormData.certificate}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, certificate: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Charges (₹)</label>
                        <input
                          type="text"
                          value={(() => {
                            // Calculate certificate cost based on diamonds and certificate status
                            if (editFormData.certificate === 'Yes') {
                              const stones = editFormData.stones || [];
                              const totalDiamondCarats = stones.reduce((total, stone) => {
                                const material = materials.find(m => m.code === stone.stoneCode);
                                const isDiamond = material ? material.category === 'Diamond'
                                  : !!(stone.stoneCode && stone.stoneCode.toUpperCase().includes('DIA'));
                                if (isDiamond) {
                                  let weight = parseFloat(stone.weight) || 0;
                                  // Convert to carats if needed
                                  if (stone.unit === 'gram') {
                                    weight = weight * 5; // 1 gram = 5 carats
                                  } else if (stone.unit === 'kg') {
                                    weight = weight * 5000;
                                  }
                                  return total + weight;
                                }
                                return total;
                              }, 0);
                              const certificateCost = Math.ceil(totalDiamondCarats * 750);
                              return `₹${certificateCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            }
                            return '₹0.00';
                          })()}
                          readOnly
                          className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          title="Calculated automatically: Diamond carats × ₹750 (only if Certificate = Yes)"
                        />
                      </div>



                    </div>
                  </div>

                  {/* Stones Information */}
                  <div className="mb-6 bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-indigo-800">Stones Information</h4>
                      <button
                        type="button"
                        onClick={() => {
                          const newStone = { id: `${Date.now()}-${Math.random()}`, stoneCode: '', stoneName: '', weight: '', rate: 0, unit: 'gram', stoneCost: 0 };
                          lastAddedEditStoneId.current = newStone.id;
                          setEditFormData(prev => ({ ...prev, stones: [newStone, ...(prev.stones || [])] }));
                        }}
                        className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        Add Stone
                      </button>
                    </div>

                    {(!editFormData.stones || editFormData.stones.length === 0) ? (
                      <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-indigo-300">
                        <p className="text-indigo-600 font-medium">No stones added yet</p>
                        <p className="text-indigo-500 text-sm">Click "Add Stone" to start</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(editFormData.stones || []).map((stone, index) => (
                          <div key={stone.id || index} className="bg-white p-3 rounded-lg border border-indigo-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                              {/* Material Code typeahead */}
                              <div className="relative">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Material Code</label>
                                <input
                                  ref={el => { editStoneInputRefs.current[stone.id] = el; }}
                                  type="text"
                                  autoComplete="off"
                                  value={editStoneSearch[stone.id] !== undefined ? editStoneSearch[stone.id] : (stone.stoneCode || '')}
                                  onChange={(e) => {
                                    const q = e.target.value.toUpperCase();
                                    setEditStoneSearch(prev => ({ ...prev, [stone.id]: q }));
                                    setEditStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                                    const exactMatch = materials.find(m => m.code && m.code.toUpperCase() === q);
                                    if (exactMatch) {
                                      const materialUnit = exactMatch.unit ? ({ 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[exactMatch.unit.toUpperCase()] || 'gram') : 'gram';
                                      const newStones = [...(editFormData.stones || [])];
                                      newStones[index] = { ...newStones[index], stoneCode: exactMatch.code, stoneName: exactMatch.name || '', unit: materialUnit, rate: exactMatch.cost_price || 0, stoneCost: (parseFloat(newStones[index].weight) || 0) * (exactMatch.cost_price || 0) };
                                      const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                      const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                                      setEditFormData(prev => ({ ...prev, stones: newStones, stone_weight: totalStoneWeightGrams, net_weight: calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight), total_stone_cost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0) }));
                                      setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                    } else {
                                      setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: q.length > 0 }));
                                    }
                                  }}
                                  onFocus={() => {
                                    const q = editStoneSearch[stone.id] !== undefined ? editStoneSearch[stone.id] : (stone.stoneCode || '');
                                    setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: q.length > 0 }));
                                  }}
                                  onBlur={() => {
                                    setTimeout(() => {
                                      const q = (editStoneSearch[stone.id] || '').toUpperCase();
                                      const filtered = materials.filter(m => m.code && m.code.toUpperCase().startsWith(q));
                                      if (filtered.length === 1 && q.length > 0) {
                                        const material = filtered[0];
                                        const materialUnit = material.unit ? ({ 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[material.unit.toUpperCase()] || 'gram') : 'gram';
                                        const newStones = [...(editFormData.stones || [])];
                                        newStones[index] = { ...newStones[index], stoneCode: material.code, stoneName: material.name || '', unit: materialUnit, rate: material.cost_price || 0, stoneCost: (parseFloat(newStones[index].weight) || 0) * (material.cost_price || 0) };
                                        const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                        const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                                        setEditFormData(prev => ({ ...prev, stones: newStones, stone_weight: totalStoneWeightGrams, net_weight: calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight), total_stone_cost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0) }));
                                        setEditStoneSearch(prev => ({ ...prev, [stone.id]: material.code }));
                                      }
                                      setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                      setEditStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                                    }, 150);
                                  }}
                                  onKeyDown={(e) => {
                                    if (!editStoneDropdownOpen[stone.id]) return;
                                    const q = (editStoneSearch[stone.id] || '').toUpperCase();
                                    const filtered = materials.filter(m => m.code && m.code.toUpperCase().startsWith(q));
                                    const hi = editStoneHighlight[stone.id] !== undefined ? editStoneHighlight[stone.id] : -1;
                                    if (e.key === 'ArrowDown') { e.preventDefault(); setEditStoneHighlight(prev => ({ ...prev, [stone.id]: Math.min(hi + 1, filtered.length - 1) })); }
                                    else if (e.key === 'ArrowUp') { e.preventDefault(); setEditStoneHighlight(prev => ({ ...prev, [stone.id]: Math.max(hi - 1, 0) })); }
                                    else if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const material = filtered[hi >= 0 ? hi : 0];
                                      if (material) {
                                        const materialUnit = material.unit ? ({ 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[material.unit.toUpperCase()] || 'gram') : 'gram';
                                        const newStones = [...(editFormData.stones || [])];
                                        newStones[index] = { ...newStones[index], stoneCode: material.code, stoneName: material.name || '', unit: materialUnit, rate: material.cost_price || 0, stoneCost: (parseFloat(newStones[index].weight) || 0) * (material.cost_price || 0) };
                                        const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                        const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                                        setEditFormData(prev => ({ ...prev, stones: newStones, stone_weight: totalStoneWeightGrams, net_weight: calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight), total_stone_cost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0) }));
                                        setEditStoneSearch(prev => ({ ...prev, [stone.id]: material.code }));
                                        setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                      }
                                    } else if (e.key === 'Escape') { setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false })); }
                                  }}
                                  className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                                  placeholder="Type code…"
                                />
                                {editStoneDropdownOpen[stone.id] && (() => {
                                  const q = (editStoneSearch[stone.id] || '').toUpperCase();
                                  const filtered = materials.filter(m => m.code && m.code.toUpperCase().startsWith(q));
                                  if (filtered.length === 0) return null;
                                  const hi = editStoneHighlight[stone.id] !== undefined ? editStoneHighlight[stone.id] : -1;
                                  return (
                                    <ul className="absolute z-50 left-0 right-0 bg-white border border-indigo-300 rounded shadow-lg max-h-48 overflow-y-auto text-sm mt-0.5">
                                      {filtered.map((material, idx) => (
                                        <li
                                          key={material.id}
                                          onMouseDown={() => {
                                            const materialUnit = material.unit ? ({ 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[material.unit.toUpperCase()] || 'gram') : 'gram';
                                            const newStones = [...(editFormData.stones || [])];
                                            newStones[index] = { ...newStones[index], stoneCode: material.code, stoneName: material.name || '', unit: materialUnit, rate: material.cost_price || 0, stoneCost: (parseFloat(newStones[index].weight) || 0) * (material.cost_price || 0) };
                                            const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                            const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                                            setEditFormData(prev => ({ ...prev, stones: newStones, stone_weight: totalStoneWeightGrams, net_weight: calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight), total_stone_cost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0) }));
                                            setEditStoneSearch(prev => ({ ...prev, [stone.id]: material.code }));
                                            setEditStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                          }}
                                          className={`px-3 py-1.5 cursor-pointer flex gap-2 ${idx === hi ? 'bg-indigo-100' : 'hover:bg-indigo-50'}`}
                                        >
                                          <span className="font-medium text-indigo-700 w-20 flex-shrink-0">{material.code}</span>
                                          <span className="text-gray-600 truncate">{material.name}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  );
                                })()}
                              </div>

                              {/* Stone Name */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Material Name</label>
                                <input
                                  type="text"
                                  value={stone.stoneName || ''}
                                  onChange={(e) => {
                                    const newStones = [...(editFormData.stones || [])];
                                    newStones[index] = { ...newStones[index], stoneName: e.target.value };
                                    setEditFormData(prev => ({ ...prev, stones: newStones }));
                                  }}
                                  className="w-full p-2 text-sm border border-gray-300 rounded"
                                  placeholder="Material name"
                                />
                              </div>

                              {/* Weight + Unit */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
                                <div className="flex gap-1">
                                  <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    value={stone.weight || ''}
                                    onChange={(e) => {
                                      const newStones = [...(editFormData.stones || [])];
                                      newStones[index] = { ...newStones[index], weight: e.target.value, stoneCost: (parseFloat(e.target.value) || 0) * (parseFloat(newStones[index].rate) || 0) };
                                      const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                      const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                                      setEditFormData(prev => ({ ...prev, stones: newStones, stone_weight: totalStoneWeightGrams, net_weight: calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight), total_stone_cost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0) }));
                                    }}
                                    className="w-full p-2 text-sm border border-gray-300 rounded-l focus:ring-1 focus:ring-indigo-500"
                                    placeholder="0.000"
                                  />
                                  <select
                                    value={stone.unit || 'gram'}
                                    onChange={(e) => {
                                      const newStones = [...(editFormData.stones || [])];
                                      newStones[index] = { ...newStones[index], unit: e.target.value };
                                      const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                      const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                                      setEditFormData(prev => ({ ...prev, stones: newStones, stone_weight: totalStoneWeightGrams, net_weight: calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight) }));
                                    }}
                                    className="p-2 text-xs border border-l-0 border-gray-300 rounded-r bg-gray-50"
                                  >
                                    <option value="gram">g</option>
                                    <option value="carat">ct</option>
                                    <option value="number">nos</option>
                                  </select>
                                </div>
                              </div>

                              {/* Cost Price */}
                              {showCostPrice && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Rate (₹)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={stone.rate || ''}
                                    onChange={(e) => {
                                      const newStones = [...(editFormData.stones || [])];
                                      newStones[index] = { ...newStones[index], rate: e.target.value, stoneCost: (parseFloat(newStones[index].weight) || 0) * (parseFloat(e.target.value) || 0) };
                                      setEditFormData(prev => ({ ...prev, stones: newStones, total_stone_cost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0)), 0) }));
                                    }}
                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                                    placeholder="0.00"
                                  />
                                </div>
                              )}

                              {/* Stone Cost display */}
                              {showCostPrice && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Cost (₹)</label>
                                  <div className="p-2 text-sm bg-green-50 border border-green-200 rounded text-green-700 font-medium">
                                    ₹{((parseFloat(stone.weight) || 0) * (parseFloat(stone.rate) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                </div>
                              )}

                              {/* Remove button */}
                              <div className="flex items-end">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveEditStone(index)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded border border-red-200"
                                  title="Remove stone"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right One-Third - Image Section */}
                <div className="lg:col-span-1">
                  <div className="sticky top-4">
                    <h4 className="text-lg font-semibold text-gray-700 mb-3">Item Image</h4>
                    <div className="flex justify-center">
                      <div className="w-full max-w-sm h-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 relative">
                        {/* Hidden file input */}
                        <input
                          type="file"
                          ref={setImageFileInput}
                          onChange={handleImageFileSelect}
                          accept="image/*"
                          className="hidden"
                        />

                        {/* Display current or preview image */}
                        {(imagePreviewUrl || editFormData.image_url) ? (
                          <>
                            <img
                              src={resolveImageUrl(imagePreviewUrl || editFormData.image_url)}
                              alt={editFormData.description || 'Jewelry item'}
                              className="w-full h-full object-contain rounded-lg bg-white"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            {/* Remove image button */}
                            <div className="absolute top-2 right-2">
                              <button
                                onClick={handleRemoveImage}
                                className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 text-xs"
                                title="Remove image"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center text-gray-500">
                            <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm">No image available</p>
                            <p className="text-xs text-gray-400 mt-1">Click + to upload</p>
                          </div>
                        )}

                        {/* Upload/Change image button */}
                        <div className="absolute bottom-2 right-2">
                          <button
                            onClick={handleImageButtonClick}
                            disabled={uploadingImage}
                            className="p-1 bg-blue-600 text-white rounded-full hover:bg-blue-700 text-xs disabled:opacity-50"
                            title={imagePreviewUrl || editFormData.image_url ? "Change image" : "Upload image"}
                          >
                            {uploadingImage ? (
                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Upload controls */}
                    {selectedImageFile && (
                      <div className="mt-3 space-y-2">
                        <div className="text-sm text-gray-600">
                          <p className="font-medium">Selected file:</p>
                          <p className="truncate">{selectedImageFile.name}</p>
                          <p className="text-xs">{(selectedImageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleImageUpload}
                            disabled={uploadingImage}
                            className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {uploadingImage ? 'Uploading...' : 'Upload Image'}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedImageFile(null);
                              setImagePreviewUrl(editFormData.image_url);
                              if (imageFileInput) imageFileInput.value = '';
                            }}
                            disabled={uploadingImage}
                            className="px-3 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-700">Certificates</h4>
                        <span className="text-xs text-gray-500">{certificateDocuments.length} stored</span>
                      </div>
                      <input
                        type="file"
                        ref={setCertificateFileInput}
                        onChange={handleCertificateFileSelect}
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        className="hidden"
                      />
                      {certificateDocuments.length > 0 ? (
                        <div className="space-y-1 mb-3">
                          {certificateDocuments.map(document => (
                            <a
                              key={document.id || document.url}
                              href={resolveImageUrl(document.url)}
                              target="_blank"
                              rel="noreferrer"
                              className="block truncate text-xs text-blue-600 hover:underline"
                              title={document.filename}
                            >
                              {document.filename}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600 mb-3">No certificate attached</p>
                      )}
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            onClick={() => certificateFileInput?.click()}
                            disabled={uploadingCertificate}
                            className="w-full px-3 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {uploadingCertificate ? 'Uploading...' : 'Choose Certificate'}
                          </button>
                          {selectedCertificateFile && (
                            <div className="mt-2 space-y-2">
                              <p className="truncate text-xs text-gray-600" title={selectedCertificateFile.name}>
                                {selectedCertificateFile.name}
                              </p>
                              <button
                                type="button"
                                onClick={handleCertificateUpload}
                                disabled={uploadingCertificate}
                                className="w-full px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                Upload Certificate
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Cost Value - Prominently displayed before action buttons */}
              <div className="mt-8 mb-6">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl border-2 border-green-300 shadow-lg">
                  <div className="flex items-center justify-between">
                    <label className="text-xl font-bold text-green-800 mr-4">
                      TOTAL COST VALUE
                    </label>
                    <input
                      type="text"
                      value={(() => {
                        // Calculate live total cost value
                        const grossWeight = parseFloat(editFormData.gross_weight) || 0;
                        const formStones = editFormData.stones || [];

                        // Calculate net weight = gross weight - total stone weight in grams
                        const totalStoneWeightGrams = stonesTotalGrams(formStones);

                        const netWeight = calculateGoldNetWeight(grossWeight, totalStoneWeightGrams, editFormData.kundan_weight);
                        const goldRate = parseFloat(editFormData.gold_rate || 0);
                        const goldPurity = parseInt(editFormData.gold_purity || 18);
                        const purityFactor = getGoldPurityFactor(goldPurity);
                        const wastagePercentage = parseFloat(editFormData.wastage_percentage || 0);

                        const totalGoldCost = Math.ceil(netWeight * goldRate * purityFactor);
                        const wastageCharges = Math.ceil(totalGoldCost * (wastagePercentage / 100));

                        const totalStoneValue = formStones.reduce((total, stone) => {
                          const material = materials.find(m => m.code === stone.stoneCode);
                          const costPrice = stone.cost_price || stone.rate || (material?.cost_price || 0);
                          const costValue = parseFloat(stone.weight || 0) * parseFloat(costPrice || 0);
                          return total + costValue;
                        }, 0); const makingChargesPerGram = parseFloat(editFormData.making_charges || 0);
                        const totalMakingCharges = Math.ceil(netWeight * makingChargesPerGram);
                        const laborChargesPerGram = parseFloat(editFormData.labor_charges || 0);
                        const laborCharges = Math.ceil(netWeight * laborChargesPerGram);
                        const otherCosts = parseFloat(editFormData.other_costs || 0);

                        // Certificate cost for diamonds
                        let certificateCost = 0;
                        if (editFormData.certificate === 'Yes') {
                          const totalDiamondCarats = formStones.reduce((total, stone) => {
                            const material = materials.find(m => m.code === stone.stoneCode);
                            const isDiamond = material ? material.category === 'Diamond'
                              : !!(stone.stoneCode && stone.stoneCode.toUpperCase().includes('DIA'));
                            if (isDiamond) {
                              let weight = parseFloat(stone.weight) || 0;
                              if (stone.unit === 'gram') {
                                weight = weight * 5; // 1 gram = 5 carats
                              } else if (stone.unit === 'kg') {
                                weight = weight * 5000;
                              }
                              return total + weight;
                            }
                            return total;
                          }, 0);
                          certificateCost = Math.ceil(totalDiamondCarats * 750);
                        }

                        const totalCostValue = totalGoldCost + wastageCharges + totalStoneValue + totalMakingCharges + laborCharges + otherCosts + certificateCost;
                        return `₹${totalCostValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      })()}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, total_cost_value: e.target.value }))}
                      className="flex-1 p-4 text-2xl font-bold text-green-800 bg-white border-2 border-green-400 rounded-lg shadow-inner focus:ring-4 focus:ring-green-200 focus:border-green-500 text-right"
                      readOnly
                      title="Calculated field - Final total cost of the jewelry piece"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePrintTag(selectedItem.id, selectedItem.code)}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 flex items-center"
                >
                  <Printer size={16} className="mr-2" />
                  Print Tag
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <PageIdentifier pageId={SCREEN_IDS?.INVENTORY?.DELETE_CONFIRMATION_MODAL || 'INV-001M3'} pageName="Delete Confirmation" isModal={true} />
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Delete Item</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete item "{selectedItem.code}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {saving ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Modal */}
      {confirmUpdateModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <PageIdentifier pageId={SCREEN_IDS?.INVENTORY?.UPDATE_CONFIRMATION_MODAL || 'INV-001M4'} pageName="Update Confirmation" isModal={true} />
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <Edit2 className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Confirm Update</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to update item "{selectedItem.code}"? This will save all changes to the database.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setConfirmUpdateModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpdate}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Updating...' : 'Confirm Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Send to Repair Modal ─────────────────────────────────────────── */}
      {repairModalOpen && repairItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center gap-2 mb-4">
              <Wrench size={20} className="text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">Send to Repair</h3>
              <span className="ml-auto text-sm text-gray-500 font-mono">{repairItem.code}</span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Repair Vendor</label>
                <select
                  value={repairForm.repair_vendor_id}
                  onChange={e => setRepairForm(p => ({ ...p, repair_vendor_id: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Select vendor —</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Sent</label>
                  <input type="date" value={repairForm.repair_sent_date}
                    onChange={e => setRepairForm(p => ({ ...p, repair_sent_date: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Return</label>
                  <input type="date" value={repairForm.repair_expected_return}
                    onChange={e => setRepairForm(p => ({ ...p, repair_expected_return: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Cost (₹)</label>
                <input type="number" min="0" step="0.01" value={repairForm.repair_cost_estimate}
                  onChange={e => setRepairForm(p => ({ ...p, repair_cost_estimate: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} value={repairForm.repair_notes}
                  onChange={e => setRepairForm(p => ({ ...p, repair_notes: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the repair needed..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setRepairModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSendToRepair} disabled={repairSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {repairSaving && <RefreshCw size={14} className="animate-spin" />}
                Send to Repair
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Return from Repair Modal ──────────────────────────────────────── */}
      {returnRepairModalOpen && repairItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center gap-2 mb-4">
              <RotateCcw size={20} className="text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-800">Return from Repair</h3>
              <span className="ml-auto text-sm text-gray-500 font-mono">{repairItem.code}</span>
            </div>
            {repairItem.repair_notes && (
              <div className="mb-3 p-2 bg-blue-50 rounded text-sm text-blue-800">
                <strong>Repair notes:</strong> {repairItem.repair_notes}
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
                <input type="date" value={returnRepairForm.repair_return_date}
                  onChange={e => setReturnRepairForm(p => ({ ...p, repair_return_date: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actual Cost (₹)</label>
                <input type="number" min="0" step="0.01" value={returnRepairForm.repair_actual_cost}
                  onChange={e => setReturnRepairForm(p => ({ ...p, repair_actual_cost: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea rows={2} value={returnRepairForm.repair_notes}
                  onChange={e => setReturnRepairForm(p => ({ ...p, repair_notes: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  placeholder="Any notes on the returned item..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setReturnRepairModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleReturnFromRepair} disabled={repairSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2">
                {repairSaving && <RefreshCw size={14} className="animate-spin" />}
                Mark as Returned
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default ViewInventory;
